import { processError } from '@/helpers';
import { updatePresentation } from '@/requests';
import { CodeHighlightNode, CodeNode } from '@lexical/code';
import { HashtagNode } from '@lexical/hashtag';
import { $generateNodesFromDOM } from '@lexical/html';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { MarkNode } from '@lexical/mark';
import { $convertFromMarkdownString, $convertToMarkdownString } from '@lexical/markdown';
import { OverflowNode } from '@lexical/overflow';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import {
  ActionIcon,
  Box,
  Button,
  CloseButton,
  getThemeColor,
  Group,
  Loader,
  LoadingOverlay,
  Popover,
  rem,
  Stack,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDebouncedCallback } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconBrandNotion,
  IconCheck,
  IconMarkdown,
  IconPencil,
  IconTypography,
  IconUnlink,
} from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  $getRoot,
  $insertNodes,
  $isTextNode,
  $setSelection,
  DOMConversionMap,
  DOMExportOutput,
  DOMExportOutputMap,
  Klass,
  LexicalEditor,
  LexicalNode,
  ParagraphNode,
  TextNode,
} from 'lexical';
import { useRef, useState } from 'react';
import { DocxUpload } from '../Presentation/DocxUpload';
import LexicalAutoLinkPlugin from './AutoLinkPlugin';
import CodeHighlightPlugin from './CodeHighlightPlugin';
import DragDropPaste from './DragDropPastePlugin';
import { EquationNode } from './EquationNode';
import EquationsPlugin from './EquationsPlugin';
import ExampleTheme from './ExampleTheme';
import { ImageNode } from './ImageNode';
import ImagesPlugin from './ImagesPlugin';
import LinkPlugin from './LinkPlugin';
import { PLAYGROUND_TRANSFORMERS } from './MarkdownShortcutPlugin';
import { parseAllowedColor, parseAllowedFontSize } from './styleConfig';
import TableCellActionMenuPlugin from './TableCellActionMenuPlugin';
import TableHoverActionsPlugin from './TableHoverActionsPlugin';
import ToolbarPlugin from './ToolbarPlugin';
import { transformLexicalToSlides } from './utils';
import { VideoNode } from './VideoNode';
import VideoPlugin from './VideoPlugin';
import { CalloutNode } from './CalloutNode';
import { BookmarkNode } from './BookmarkNode';
import CollapsiblePlugin from './CollapsiblePlugin';
import { CollapsibleContainerNode } from './CollapsibleContainerNode';
import { CollapsibleContentNode } from './CollapsibleContentNode';
import { CollapsibleTitleNode } from './CollapsibleTitleNode';
import AutoEmbedPlugin from './AutoEmbedPlugin';
import { EmbedNode } from './EmbedNode';

const removeStylesExportDOM = (editor: LexicalEditor, target: LexicalNode): DOMExportOutput => {
  const output = target.exportDOM(editor);
  if (output && output.element instanceof HTMLElement) {
    // Remove all inline styles and classes if the element is an HTMLElement
    // Children are checked as well since TextNode can be nested
    // in i, b, and strong tags.
    for (const el of [
      output.element,
      ...output.element.querySelectorAll('[style],[class],[dir="ltr"]'),
    ]) {
      el.removeAttribute('class');
      el.removeAttribute('style');
      if (el.getAttribute('dir') === 'ltr') {
        el.removeAttribute('dir');
      }
    }
  }
  return output;
};

const exportMap: DOMExportOutputMap = new Map<
  Klass<LexicalNode>,
  (editor: LexicalEditor, target: LexicalNode) => DOMExportOutput
>([
  [ParagraphNode, removeStylesExportDOM],
  [TextNode, removeStylesExportDOM],
]);

const getExtraStyles = (element: HTMLElement): string => {
  // Parse styles from pasted input, but only if they match exactly the
  // sort of styles that would be produced by exportDOM
  let extraStyles = '';
  const fontSize = parseAllowedFontSize(element.style.fontSize);
  const backgroundColor = parseAllowedColor(element.style.backgroundColor);
  const color = parseAllowedColor(element.style.color);
  if (fontSize !== '' && fontSize !== '15px') {
    extraStyles += `font-size: ${fontSize};`;
  }
  if (backgroundColor !== '' && backgroundColor !== 'rgb(255, 255, 255)') {
    extraStyles += `background-color: ${backgroundColor};`;
  }
  if (color !== '' && color !== 'rgb(0, 0, 0)') {
    extraStyles += `color: ${color};`;
  }
  return extraStyles;
};

const constructImportMap = (): DOMConversionMap => {
  const importMap: DOMConversionMap = {};

  // Wrap all TextNode importers with a function that also imports
  // the custom styles implemented by the playground
  for (const [tag, fn] of Object.entries(TextNode.importDOM() || {})) {
    importMap[tag] = (importNode) => {
      const importer = fn(importNode);
      if (!importer) {
        return null;
      }
      return {
        ...importer,
        conversion: (element) => {
          const output = importer.conversion(element);
          if (
            output === null ||
            output.forChild === undefined ||
            output.after !== undefined ||
            output.node !== null
          ) {
            return output;
          }
          const extraStyles = getExtraStyles(element);
          if (extraStyles) {
            const { forChild } = output;
            return {
              ...output,
              forChild: (child, parent) => {
                const textNode = forChild(child, parent);
                if ($isTextNode(textNode)) {
                  textNode.setStyle(textNode.getStyle() + extraStyles);
                }
                return textNode;
              },
            };
          }
          return output;
        },
      };
    };
  }

  return importMap;
};

const editorConfig = {
  html: {
    export: exportMap,
    import: constructImportMap(),
  },
  namespace: 'React.js Demo',
  nodes: [
    ParagraphNode,
    TextNode,
    HeadingNode,
    ListNode,
    ListItemNode,
    QuoteNode,
    CodeNode,
    ImageNode,
    TableNode,
    VideoNode,
    CalloutNode,
    BookmarkNode,
    EmbedNode,
    TableCellNode,
    TableRowNode,
    HashtagNode,
    CodeHighlightNode,
    AutoLinkNode,
    LinkNode,
    OverflowNode,
    HorizontalRuleNode,
    MarkNode,
    EquationNode,
    CollapsibleContainerNode,
    CollapsibleContentNode,
    CollapsibleTitleNode,
  ],
  onError(error: Error) {
    throw error;
  },
  theme: ExampleTheme,
};

function MarkdownEditor({ markdownString, setMarkdownString }) {
  const [editor] = useLexicalComposerContext();

  return (
    <Textarea
      value={markdownString}
      h="100%"
      styles={{
        wrapper: { height: '100%' },
        input: { fontFamily: 'var(--mantine-font-family-monospace)', height: '100%' },
      }}
      variant="unstyled"
      mb="md"
      placeholder="Enter Markdown content"
      onChange={(event) =>
        editor.update(() => {
          setMarkdownString(event.currentTarget.value);
          $convertFromMarkdownString(
            event.currentTarget.value,
            PLAYGROUND_TRANSFORMERS,
            undefined, // node
            true
          );
          $setSelection(null);
        })
      }
    />
  );
}

function isNotionUrl(value) {
  return value.startsWith('https://www.notion.so');
}

export function NotionImport({ presentation, user }) {
  const [opened, setOpened] = useState(false);
  const queryClient = useQueryClient();

  const form = useForm({
    mode: 'uncontrolled',
    validate: {
      external_document_id: (value) =>
        !value
          ? 'Please enter a Notion page URL'
          : !isNotionUrl(value)
            ? 'Please enter a valid Notion page URL'
            : null,
    },
    initialValues: {
      external_document_id: presentation.external_document_id,
    },
  });

  const updatePresentationMutation = useMutation({
    mutationFn: updatePresentation,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['presentation', presentation.presentation_id] });
      form.setValues({ external_document_id: data.external_document_id });
      form.resetDirty({ external_document_id: data.external_document_id });
      notifications.show({
        message: 'Successfully updated presentation.',
      });
    },
    onError: processError,
  });

  const notionAuthUrl = () => {
    const state = JSON.stringify({
      notion_url: form.getValues().external_document_id,
    });
    const redirectUri = `${window.location.protocol}//${window.location.host}/notion/fetch_access_token`;
    const encodedRedirectUri = encodeURIComponent(redirectUri);

    return `https://api.notion.com/v1/oauth/authorize?client_id=62c9517d-f880-4b2d-b270-65eb19840ff0&response_type=code&owner=user&state=${state}&redirect_uri=${encodedRedirectUri}`;
  };

  return (
    <>
      <Title order={5}>Notion URL</Title>
      <Group gap="0">
        <Text truncate="end" w={{ base: 120, sm: 'auto' }} style={{ flex: 1 }}>
          {presentation.origin === 'notion' && presentation.external_document_id
            ? presentation.external_document_id
            : 'Not currently linked to Notion'}
        </Text>
        <Group gap="5px">
          {user && user.user_id === presentation.user_id ? (
            <Popover
              width={400}
              trapFocus
              position="bottom-end"
              shadow="md"
              opened={opened}
              onChange={setOpened}
            >
              <Popover.Target>
                <ActionIcon
                  size={36}
                  variant="filled"
                  aria-label="Edit Notion URL"
                  onClick={() => setOpened((o) => !o)}
                >
                  <IconPencil style={{ width: '70%', height: '70%' }} stroke={1.5} />
                </ActionIcon>
              </Popover.Target>
              <Popover.Dropdown>
                <Group align="center">
                  <TextInput
                    style={{ flexGrow: 1 }}
                    key={form.key('external_document_id')}
                    {...form.getInputProps('external_document_id')}
                    placeholder="Enter full Notion URL"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        if (form.isDirty('external_document_id')) {
                          updatePresentationMutation.mutate({
                            ...form.getValues(),
                            origin: 'notion',
                            id: presentation.presentation_id,
                          });
                        }
                      }
                    }}
                  />
                  <ActionIcon
                    size={36}
                    variant="filled"
                    aria-label="Edit Notion URL"
                    onClick={() => {
                      if (form.isDirty('external_document_id')) {
                        setOpened(false);
                        modals.openConfirmModal({
                          title: 'Are you sure?',
                          children: (
                            <Text size="sm">
                              This will replace your current presentation content. Are you sure you
                              want to continue?
                            </Text>
                          ),
                          labels: { confirm: 'Confirm', cancel: 'Cancel' },
                          onConfirm: () =>
                            updatePresentationMutation.mutate({
                              ...form.getValues(),
                              origin: 'notion',
                              id: presentation.presentation_id,
                            }),
                        });
                      }
                    }}
                  >
                    <IconCheck style={{ width: '70%', height: '70%' }} stroke={1.5} />
                  </ActionIcon>
                </Group>
              </Popover.Dropdown>
            </Popover>
          ) : (
            <ActionIcon
              component="a"
              size={36}
              variant="filled"
              aria-label="Edit Notion URL"
              href="/users/sign_in"
            >
              <IconPencil style={{ width: '70%', height: '70%' }} stroke={1.5} />
            </ActionIcon>
          )}
          <Tooltip label="Unlink from Notion">
            {user && user.user_id === presentation.user_id ? (
              <ActionIcon
                size={36}
                variant="filled"
                aria-label="Unlink from Notion"
                onClick={() => {
                  updatePresentationMutation.mutate({
                    external_document_id: null,
                    origin: 'app',
                    id: presentation.presentation_id,
                  });
                }}
              >
                <IconUnlink style={{ width: '70%', height: '70%' }} stroke={1.5} />
              </ActionIcon>
            ) : (
              <ActionIcon
                component="a"
                size={36}
                variant="filled"
                aria-label="Unlink from Notion"
                href="/users/sign_in"
              >
                <IconUnlink style={{ width: '70%', height: '70%' }} stroke={1.5} />
              </ActionIcon>
            )}
          </Tooltip>
        </Group>
      </Group>
    </>
  );
}
function RichTextEditorInterface({
  presentation,
  user,
  setUpdateFunction,
  setPresentationSlides,
  closeSidenav,
  viewMode,
  toggleViewMode,
}) {
  const [editor] = useLexicalComposerContext();
  const { colorScheme } = useMantineColorScheme();
  const theme = useMantineTheme();
  const [markdownString, setMarkdownString] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState('richtext');
  const anchorRef = useRef(null);

  function updateEditorStateFromHtml(htmlString) {
    editor.update(() => {
      const parser = new DOMParser();
      const dom = parser.parseFromString(htmlString, 'text/html');
      const nodes = $generateNodesFromDOM(editor, dom);
      $getRoot().select();
      $insertNodes(nodes);
      setCurrentTab('richtext');
    });
  }

  function updateEditorState(lexicalPayload) {
    const editorState = editor.parseEditorState(lexicalPayload);
    editor.setEditorState(editorState);
  }

  const updatePresentationMutation = useMutation({
    mutationFn: updatePresentation,
    onSuccess: () => {
      setUpdateFunction(false);
    },
    onError: processError,
  });

  const debouncedUpdatePresentation = useDebouncedCallback((presentationParams) => {
    updatePresentationMutation.mutate(presentationParams);
  }, 1500);

  if (loading) {
    return (
      <LoadingOverlay
        visible={true}
        loaderProps={{ children: <Loader color="#FE9920" type="bars" /> }}
      />
    );
  }

  return (
    <Tabs
      value={currentTab}
      defaultValue="richtext"
      h="100%"
      onChange={(nextActiveTab) => {
        if (nextActiveTab === 'markdown')
          editor.read(() =>
            setMarkdownString(
              $convertToMarkdownString(
                PLAYGROUND_TRANSFORMERS,
                undefined, // node
                true
              )
            )
          );
        setCurrentTab(nextActiveTab);
      }}
    >
      <Stack
        h="100%"
        bg={
          colorScheme === 'light' ? getThemeColor('white', theme) : getThemeColor('dark.7', theme)
        }
      >
        <Group justify="space-between">
          <Button variant="outline" onClick={toggleViewMode}>
            {viewMode.charAt(0).toUpperCase() + viewMode.slice(1)}
          </Button>
          <CloseButton size="md" onClick={closeSidenav} />
        </Group>
        <Tabs.List>
          <Tabs.Tab
            value="richtext"
            leftSection={<IconTypography style={{ width: rem(18), height: rem(18) }} />}
          >
            Rich Text
          </Tabs.Tab>
          <Tabs.Tab
            value="markdown"
            leftSection={<IconMarkdown style={{ width: rem(18), height: rem(18) }} />}
          >
            Markdown
          </Tabs.Tab>
          <Tabs.Tab
            value="docx"
            leftSection={<IconMarkdown style={{ width: rem(18), height: rem(18) }} />}
          >
            DOCX
          </Tabs.Tab>
          <Tabs.Tab
            value="notion"
            leftSection={<IconBrandNotion style={{ width: rem(18), height: rem(18) }} />}
          >
            Notion
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel
          value="richtext"
          h="100%"
          mx={0}
          my={0}
          pos="relative"
          style={{
            borderRadius: 2,
            maxWidth: 600,
            lineHeight: '20px',
            fontWeight: 400,
            textAlign: 'left',
            borderTopLeftRadius: 10,
            borderTopRightRadius: 10,
          }}
        >
          {presentation.origin === 'notion' && presentation.external_document_id ? (
            <Text>
              This presentation is currently linked to Notion. Please unlink it from the Notion tab
              to edit.
            </Text>
          ) : (
            <Stack h="100%">
              <ToolbarPlugin />
              <RichTextPlugin
                contentEditable={
                  <Box
                    className="ContentEditable__root"
                    pos="relative"
                    style={{
                      minHeight: '150px',
                      height: '150px',
                      flexGrow: 1,
                      overflow: 'auto',
                    }}
                    ref={anchorRef}
                  >
                    <ContentEditable
                      style={{
                        resize: 'none',
                        fontSize: '15px',
                        position: 'relative',
                        tabSize: 1,
                        outline: 0,
                        caretColor:
                          colorScheme === 'light'
                            ? getThemeColor('dark.7', theme)
                            : getThemeColor('white', theme),
                        color:
                          colorScheme === 'light'
                            ? getThemeColor('dark.7', theme)
                            : getThemeColor('white', theme),
                        height: '100%',
                      }}
                    />
                  </Box>
                }
                ErrorBoundary={LexicalErrorBoundary}
              />
              <OnChangePlugin
                onChange={(editorState) => {
                  const editorStateString = JSON.stringify(editorState);
                  setUpdateFunction(true);
                  debouncedUpdatePresentation({
                    id: presentation.presentation_id,
                    raw_content:
                      presentation.origin === 'notion' && presentation.external_document_id
                        ? null
                        : editorStateString,
                  });
                  setPresentationSlides(transformLexicalToSlides(editorStateString));
                }}
              />
              <HistoryPlugin />
              <AutoFocusPlugin />
              <ListPlugin />
              <CheckListPlugin />
              <LexicalAutoLinkPlugin />
              <LinkPlugin hasLinkAttributes />
              <CodeHighlightPlugin />
              <MarkdownShortcutPlugin />
              <TablePlugin hasCellMerge hasCellBackgroundColor />
              <VideoPlugin />
              <AutoEmbedPlugin />
              <ImagesPlugin />
              <DragDropPaste />
              <EquationsPlugin />
              <TableCellActionMenuPlugin anchorRef={anchorRef} cellMerge={true} />
              <TableHoverActionsPlugin />
              <CollapsiblePlugin />
            </Stack>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="markdown" h="100%">
          {presentation.origin === 'notion' && presentation.external_document_id ? (
            <Text>
              This presentation is currently linked to Notion and cannot be edited. Please unlink to
              edit it.
            </Text>
          ) : (
            <MarkdownEditor markdownString={markdownString} setMarkdownString={setMarkdownString} />
          )}
        </Tabs.Panel>

        <Tabs.Panel value="docx" h="100%">
          {presentation.origin === 'notion' && presentation.external_document_id ? (
            <Text>
              This presentation is currently linked to Notion and cannot be edited. Please unlink to
              edit it.
            </Text>
          ) : (
            <DocxUpload updateEditorStateFromHtml={updateEditorStateFromHtml} />
          )}
        </Tabs.Panel>

        <Tabs.Panel value="notion" h="100%">
          <NotionImport presentation={presentation} user={user} />
        </Tabs.Panel>
      </Stack>
    </Tabs>
  );
}

export default function RichTextEditor({
  presentation,
  setUpdateFunction,
  closeSidenav,
  setPresentationSlides,
  toggleViewMode,
  viewMode,
  user,
}) {
  return (
    <LexicalComposer
      initialConfig={{ ...editorConfig, ...{ editorState: presentation.raw_content } }}
    >
      <RichTextEditorInterface
        user={user}
        setPresentationSlides={setPresentationSlides}
        closeSidenav={closeSidenav}
        toggleViewMode={toggleViewMode}
        viewMode={viewMode}
        presentation={presentation}
        setUpdateFunction={setUpdateFunction}
      />
    </LexicalComposer>
  );
}
